import { Box, Grid } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { CustomizedDataGrid } from 'components/customized_data_grid';
import YearMonthPicker from 'components/Dropdown/YearMonthPicker';
import RowTypography from 'components/Table/RowTypography';
import CustomBarChartWidget from 'components/Widgets/CustomBarChart';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { fetchHappySignalBySector } from 'service/service';
import CaptureExportControls from 'utils/CaptureExportControls';

function HappySignalBySector() {
    const theme = useTheme();
    const [isLoading, setIsLoading] = React.useState(false);
    const [happinessIndexData, setHappinessIndexData] = useState<any>(null);
    const [happinessIndexBarChartOptions, setHappinessIndexBarChartOptions] = useState<any>({});
    const [lostTimeData, setLostTimeData] = useState<any>(null);
    const [lostTimeBarChartOptions, setLostTimeBarChartOptions] = useState<any>({});
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
    const [sortingInfo] = useState({ sortBy: 'size', sortDir: 'desc' });
    const [tableDataList, setTableDataList] = useState<any[]>([]);
    const [isExporting, setIsExporting] = useState(false);
    const isXOps = true;
    const getInitialMonth = () => {
        return isXOps ? moment().subtract(1, 'month').format('YYYY-MM') : '0M';
    };
    const [customMonth, setCustomMonth] = useState(getInitialMonth());
    const [filterMonth, setFilterMonth] = useState(getInitialMonth());
    const initialMonth = getInitialMonth();

    const defaultColumnNames = ['Sector', 'Happiness Index', 'Lost Time (Hours)', 'Responses'];

    const handleSorting = (newSortingInfo: any) => {
    };

    const handlepageSizeChange = (event: any) => {
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    };

    const getColumns = () => {
        const columnMap: Array<{ key: string; label: string }> = [
            { key: 'sector', label: 'Sector' },
            { key: 'happinessIndex', label: 'Happiness Index' },
            { key: 'lostTime', label: 'Lost Time (Hours)' },
            { key: 'responses', label: 'Responses' }
        ];

        const percentFields: string[] = []; // Add keys here if needed in the future
        const hourFields: string[] = ['lostTime'];

        return columnMap.map((col) => {
            let label = col.label;

            return {
                key: col.key,
                name: label,
                resizable: true,
                formatter: ({ row }: any) => {
                    let value = row[col.key];
                    if (percentFields.includes(col.key)) {
                        value = `${value}%`;
                    } else if (hourFields.includes(col.key)) {
                        value = `${value} Hrs`;
                    }
                    return <RowTypography>{value}</RowTypography>;
                }
            };
        });
    };

    useEffect(() => {
        fetchAndPopulateData(filterMonth);
    }, []);

    const getSimpleBarChartOptions = (
        data: { name: string; value: number }[],
        label: string,
        barColor: string = theme.palette.secondary.main,
        lastYearAvg: any
    ) => {
        const values = [...data.map((d) => d.value), ...lastYearAvg];

        const maxVal = Math.max(...values);
        const minVal = Math.min(...values);

        const yMax = Math.ceil(maxVal / 10) * 10;
        const yMin = Math.max(0, Math.floor(minVal / 10) * 10);

        return {
            title: { show: false },
            tooltip: {
                trigger: 'axis',
                textStyle: { color: '#A7ADCB' },
                confine: true,
                backgroundColor: '#003771',
                formatter: function (params: any[]) {
                    const sector = params[0]?.axisValueLabel;
                    let tooltipContent = `<div><strong>Sector:</strong> ${sector}</div>`;
                    params.forEach((item) => {
                        tooltipContent += `<div><strong>${item.seriesName}</strong>: ${item.value}</div>`;
                    });
                    return tooltipContent;
                }
            },
            grid: { left: '5%', right: '5%', bottom: '10%', containLabel: true },
            xAxis: {
                type: 'category',
                data: data.map((item) => item.name),
                axisLabel: {
                    color: `${theme.palette.text.subtle}`,
                    rotate: window.innerWidth < 600 ? 30 : 0
                }
            },
            yAxis: {
                type: 'value',
                min: yMin,
                max: yMax,
                splitLine: { show: false },
                axisLabel: { color: `${theme.palette.text.subtle}` }
            },
            series: [
                {
                    name: label,
                    type: 'bar',
                    data: data.map((item) => item.value),
                    itemStyle: {
                        color: barColor
                    },
                    barWidth: '30%',
                    barMaxWidth: '30%',
                    label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' }
                },
                {
                    name: `${Number(filterMonth.split('-')?.[0]) - 1} Average`,
                    label: { show: true, position: 'top', fontSize: 12, color: '#00F0F0' },
                    type: 'line',
                    data: lastYearAvg,
                    symbol: 'none',
                    color: '#FFC239',
                    lineStyle: { width: 2 }
                }
            ],
            legend: {
                orient: 'horizontal',
                bottom: '1%',
                left: '2%',
                textStyle: { color: '#A7ADCB' },
                selectedMode: false,
                itemWidth: 10,
                itemHeight: 10,
                icon: 'roundRect'
            }
        };
    };

    const fetchAndPopulateData = async (filterMonth: string) => {
        try {
            setIsLoading(true);
            const response = await fetchHappySignalBySector(filterMonth);

            if (response.success && response.data) {
                const { sectors, page, lastYearAverages } = response.data;

                // Normalize chart data
                const happinessChartData = sectors.map((item: any) => ({
                    name: item.name,
                    value: item.happinessIndex
                }));

                const lostTimeChartData = sectors.map((item: any) => ({
                    name: item.name,
                    value: item.lostTimeHours
                }));

                // Normalize table data (rename "name" to "sector" for consistency with column keys)
                const tableData = sectors.map((item: any) => ({
                    sector: item.name,
                    happinessIndex: item.happinessIndex,
                    lostTime: item.lostTimeHours,
                    responses: item.responses
                }));

                const happinessLastYearAvg = happinessChartData.map(() => lastYearAverages.happinessIndex);
                const lostTimeLastYearAvg = lostTimeChartData.map(() => lastYearAverages.lostTimeHours);


                // Set states
                setHappinessIndexData(happinessChartData);
                setLostTimeData(lostTimeChartData);
                setHappinessIndexBarChartOptions(
                    getSimpleBarChartOptions(happinessChartData, 'Happiness Index', '#FF00FF', happinessLastYearAvg)
                );

                setLostTimeBarChartOptions(
                    getSimpleBarChartOptions(lostTimeChartData, 'Lost Time (Hours)', `${theme.palette.boxColor.orange.color}`, lostTimeLastYearAvg)
                );

                setTableDataList(tableData);
                setPaginationData({
                    totalElements: page.totalElements,
                    totalPages: page.totalPages
                });
                setPage(page.currentPage);
                setPageSize(page.size);
            }
        } catch (err) {
            console.error('Failed to fetch sector-wise data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDateChange = (selectedDate: string) => {
        setFilterMonth(selectedDate);
        fetchAndPopulateData(selectedDate);
    };

    const generateTitleSuffix = () => {
        if (filterMonth !== initialMonth) {
            const isYearFilter = filterMonth?.split('-')?.filter(df => !!df)?.length === 1
            const formattedMonth = isYearFilter ? filterMonth : moment(filterMonth, 'YYYY-MM').format('MMMM YYYY');
            return ` - ${formattedMonth}`;
        }
        return '';
    };

    const dynamicSuffix = generateTitleSuffix();

    const handleExportToExcel = () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const storedPref = localStorage.getItem('Sector_Performance_Details_user_col_pref');
            const storedColumnNames: string[] | null = storedPref ? JSON.parse(storedPref) : null;
            const preferredColumnNames: string[] = storedColumnNames?.length
                ? storedColumnNames
                : defaultColumnNames;

            const allColumns = getColumns();
            const nameToKey: Record<string, string> = {};
            allColumns.forEach((col: any) => { nameToKey[col.name] = col.key; });

            const exportData = tableDataList.map((row: any) => {
                const exportRow: Record<string, any> = {};
                preferredColumnNames.forEach((colName: string) => {
                    const key = nameToKey[colName];
                    if (key !== undefined) {
                        const val = row[key];
                        exportRow[colName] = val ?? '';
                    }
                });
                return exportRow;
            });

            const escapeCsv = (v: any) => `"${String(v ?? '').replaceAll('"', '""')}"`;
            const header = preferredColumnNames.filter(n => nameToKey[n] !== undefined).join(',');
            const rows = exportData.map((r: any) =>
                preferredColumnNames
                    .filter((n: string) => nameToKey[n] !== undefined)
                    .map((n: string) => escapeCsv(r[n]))
                    .join(',')
            );
            const csv = [header, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `HappySignal_BySector_${filterMonth}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            {/* Move DateFilterDropdown to right */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <YearMonthPicker
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={handleDateChange}
                />
                {/* <DateFilterDropdown
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={handleDateChange}
                    isXOps={true}
                    boxProps={{ sx: dropDownSx }}
                /> */}
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <CustomBarChartWidget
                        title={`By Happiness Index${dynamicSuffix}`} // <--- 4. UPDATED THIS LINE
                        isLoading={isLoading}
                        data={happinessIndexData}
                        options={happinessIndexBarChartOptions}
                    />
                </Grid>
                <Grid item xs={12} md={6} sx={{display: 'flex', alignItems: 'end'}}>
                        <CustomBarChartWidget
                            title={`By Lost Time(Hours)${dynamicSuffix}`}
                            isLoading={isLoading}
                            data={lostTimeData}
                            options={lostTimeBarChartOptions}
                            sx={{flexGrow:'1'}}
                            
                        />
                         <CaptureExportControls
                                onScreenshot={() => {}}
                                screenCapturing={false}
                                onExport={handleExportToExcel}
                                isExporting={isExporting}
                                isVisible={true}
                                showExport={true}
                                type="snow"
                                exportLabel="Export to CSV"
                                sx={{ ml: 0, '& #exclude-camera': { display: 'none' } }}
                            />
                </Grid>
                <Grid item xs={12}>
                    <CustomizedDataGrid
                        title="Sector Performance Details"
                        tableType="Sector_Performance_Details"
                        tableColumns={getColumns()}
                        tableData={tableDataList}
                        defaultColumnNames={defaultColumnNames}
                        onSorting={handleSorting}
                        currSorting={sortingInfo}
                        page={page}
                        pageSize={pageSize}
                        paginationData={paginationData}
                        listLength={tableDataList?.length}
                        handlepageSizeChange={handlepageSizeChange}
                        handlePageChange={handlePageChange}
                        isDateRangeFilter={false}
                        isDisabled={false}
                        isFetching={isLoading}
                        childRowClass={true}
                        isShowMoreButton={true}
                        isSortIcon={true}
                        showExport={false}
                        isLighterTheme={true}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

export default HappySignalBySector;